import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";

/**
 * 기출 후기 SSOT — 후기 글이 있는 회차만. 최신 먼저. heroImage 배너로 렌더.
 *
 * past-exam 우측 안내/하단 폴백(`PastExamAside`, JCG-2164)과 연도 폴더 모달 상단 배너
 * (`FolderGrid`, JCG-2179)가 공용으로 소비한다. 새 후기 추가 시 이 배열만 갱신.
 *
 * `"use client"` 없는 순수 데이터/표현 모듈이라 서버 컴포넌트(`PastExams`)와
 * 클라이언트 컴포넌트(`PastExamIndexShell`) 양쪽에서 import 가능하다.
 */
export const REVIEW_LINKS = [
  {
    href: "/past-exam/2026/2026-2-review",
    title: "2026년 2회 실기 후기",
    subtitle: "합격률 결과와 회차 분석을 한눈에",
    image: "/folder-icons/past-exam/2026/2026-2.webp",
  },
  {
    href: "/past-exam/2026/2026-1-review",
    title: "2026년 1회 실기 후기",
    subtitle: "합격률 결과와 회차 분석을 한눈에",
    image: "/past-exam/2026/2026-1-review/exam-result_gamja.webp",
  },
  {
    href: "/past-exam/2025/2025-3-review",
    title: "2025년 3회 실기 후기",
    subtitle: "합격률 예상과 회차 분석을 한눈에",
    image: "/past-exam/2025/2025-3-review/santa-present_gamja-easy.webp",
  },
  {
    href: "/past-exam/2025/2025-2-review",
    title: "2025년 2회 실기 후기",
    subtitle: "합격률 결과와 회차 분석을 한눈에",
    image: "/past-exam/2025/2025-2-review/jeongcheogi-review-2025-2_thumb.webp",
  },
] as const;

/**
 * 기출 후기 heroImage 배너 — amber→orange 그라데이션 카드에 감자 hero 이미지 + 제목/부제 + "지금 보기" CTA.
 * 좁은 우측 사이드바·하단 폴백·연도 폴더 모달 상단 모두에서 가로 컴팩트 배너로 렌더된다.
 * 훅·브라우저 API 없는 순수 표현 컴포넌트라 서버/클라이언트 양쪽에서 사용 가능.
 */
export function ReviewBanner({
  review,
}: {
  review: (typeof REVIEW_LINKS)[number];
}) {
  return (
    <Link
      href={review.href}
      prefetch={false}
      className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-3 shadow-sm transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:border-amber-800 dark:from-amber-950/50 dark:to-orange-950/40"
    >
      <Image
        src={review.image}
        // 바로 옆 제목(`review.title`)과 중복되는 장식용 캐릭터 이미지 → alt 비워 SR 중복 announce 방지
        alt=""
        width={88}
        height={88}
        className="h-16 w-16 shrink-0 object-contain transition-transform group-hover:scale-105"
      />
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold leading-tight text-amber-900 dark:text-amber-100">
          {review.title}
        </h3>
        <p className="mt-0.5 text-xs leading-snug text-amber-800/80 dark:text-amber-200/80">
          {review.subtitle}
        </p>
        <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 dark:text-orange-300 group-hover:underline">
          지금 보기
          <ArrowRight
            className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </span>
      </div>
    </Link>
  );
}
